// packages/web/src/context/AuthContext.tsx
import React, {
  createContext, useContext, useEffect, useRef,
  useState, useCallback, type ReactNode,
} from 'react';
import { createClient } from '@supabase/supabase-js';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { apiClient } from '../services/api.client';

// ── Supabase client (shared across the app) ───────────────────────
export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
);

// ── Auth user shape ───────────────────────────────────────────────
// Fields are intentionally FLAT so pages can do:
//   user.first_name   user.role.name   user.organization_id
// AND also:
//   user.profile.first_name  (for backward compat with older pages)
export interface AuthUser {
  id:              string;
  email:           string;
  // Flat fields (used by DashboardPage, etc.)
  first_name:      string;
  last_name:       string;
  avatar_url:      string | null;
  organization_id: string;
  employee_id:     string | null;
  // Platform admin flag
  is_platform_admin?: boolean;
  // Nested profile alias (for pages that use user.profile.first_name)
  profile: {
    first_name:  string;
    last_name:   string;
    avatar_url:  string | null;
    employee_id?: string | null;
    // alias so user.profile.role.name works in DashboardPage
    role?: {
      id:           string;
      name:         string;
      display_name: string;
      permissions:  string[];
    };
  };
  role: {
    id:           string;
    name:         string;
    display_name: string;
    permissions:  string[];
  };
  organization: {
    id:                string;
    name:              string;
    slug:              string;
    logo_url:          string | null;
    base_currency:     string;
    primary_country:   string;
    subscription_tier: string;
  };
}

// ── Permission checker ────────────────────────────────────────────
// Maps string-based can() calls to role permissions array.
// Supports: 'payroll:view' 'payroll:manage' 'employees:manage'
//           'leaves:manage' 'org:manage'
function buildCan(role: AuthUser['role']) {
  return (permission: string): boolean => {
    return role.permissions.includes(permission);
  };
}

// ── State & context types ─────────────────────────────────────────
interface AuthState {
  user:            AuthUser | null;
  isLoading:       boolean;
  isAuthenticated: boolean;
  error:           string | null;
}

export interface AuthContextValue extends AuthState {
  login:       (email: string, password: string) => Promise<void>;
  logout:      () => Promise<void>;
  refreshUser: () => Promise<void>;
  can:         (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);
// Determine API base URL with fallbacks
const getAPIBase = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) {
    console.log('[AuthContext] Using API URL from env:', envUrl);
    return `${envUrl}/api/v1`;
  }
  
  // Fallback: use current host with port 4000
  const hostname = window.location.hostname;
  const port = 4000;
  const protocol = window.location.protocol;
  const fallbackUrl = `${protocol}//${hostname === 'localhost' || hostname === '127.0.0.1' ? hostname : 'localhost'}:${port}`;
  console.log('[AuthContext] Using fallback API URL:', fallbackUrl);
  return `${fallbackUrl}/api/v1`;
};

const API_BASE = getAPIBase();

// ── Provider ──────────────────────────────────────────────────────
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null, isLoading: true, isAuthenticated: false, error: null,
  });
  const channelRef = useRef<RealtimeChannel | null>(null);

  const loadUser = useCallback(async (token: string): Promise<void> => {
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Failed to load user');

      // Normalize response from backend which may return ResolvedUser (with permission flags)
      // or shared AuthUser (with permissions array)
      const data = json.data;
      
      // Detect if role has permission flags or permissions array
      const isResolvedUser = 'can_manage_org' in (data.role || {});
      
      let normalizedRole: AuthUser['role'];
      if (isResolvedUser) {
        // ResolvedUser format from /auth/me - convert to permissions array
        const r = data.role;
        const permissions: string[] = [
          ...(r.can_manage_org ? ['org:manage'] : []),
          ...(r.can_manage_employees ? ['employees:manage'] : []),
          ...(r.can_manage_payroll ? ['payroll:manage'] : []),
          ...(r.can_view_payroll ? ['payroll:view'] : []),
          ...(r.can_manage_leaves ? ['leaves:manage'] : []),
          ...(r.can_view_own_data ? ['view:own_data'] : []),
        ];
        normalizedRole = {
          id: r.id,
          name: r.name ?? '',
          display_name: (r.name ?? '').replace(/_/g, ' ').split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          permissions,
        };
      } else {
        // Already in shared AuthUser format
        normalizedRole = data.role;
      }

      const firstName = data.first_name ?? data.profile?.first_name ?? '';
      const lastName = data.last_name ?? data.profile?.last_name ?? '';
      const avatarUrl = data.avatar_url ?? data.profile?.avatar_url ?? null;

      const user: AuthUser = {
        id:              data.id,
        email:           data.email,
        first_name:      firstName,
        last_name:       lastName,
        avatar_url:      avatarUrl,
        organization_id: data.organization_id,
        employee_id:     data.employee_id,
        profile: { first_name: firstName, last_name: lastName, avatar_url: avatarUrl, employee_id: data.employee_id, role: normalizedRole },
        role:         normalizedRole,
        organization: data.organization,
        is_platform_admin: data.is_platform_admin === true,
      };

      setState({ user, isLoading: false, isAuthenticated: true, error: null });
    } catch (err) {
      console.error('[AuthContext] loadUser error:', err);
      setState({ user: null, isLoading: false, isAuthenticated: false, error: null });
    }
  }, []);

  // Bootstrap
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      if (session?.access_token) {
        apiClient.setToken(session.access_token);
        loadUser(session.access_token);
      } else {
        apiClient.setToken(null);
        setState(s => ({ ...s, isLoading: false }));
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (!mounted) return;
        if (session?.access_token) {
          apiClient.setToken(session.access_token);
          await loadUser(session.access_token);
        } else {
          apiClient.setToken(null);
          if (channelRef.current) {
            await supabase.removeChannel(channelRef.current);
            channelRef.current = null;
          }
          setState({ user: null, isLoading: false, isAuthenticated: false, error: null });
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [loadUser]);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      console.log('[AuthContext.login] Attempting login:', { email, api: API_BASE });
      
      // Try to reach the API
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      let res: Response;
      try {
        res = await fetch(`${API_BASE}/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
          signal: controller.signal,
          credentials: 'include',
        });
      } catch (fetchErr) {
        clearTimeout(timeoutId);
        const fetchErrorMsg = fetchErr instanceof Error ? fetchErr.message : 'Network error';
        console.error('[AuthContext.login] Fetch failed:', fetchErrorMsg);
        
        if (fetchErrorMsg.includes('abort')) {
          throw new Error('Login request timed out. Backend may not be running. Please try again.');
        } else if (fetchErrorMsg.includes('Failed to fetch')) {
          throw new Error('Cannot connect to server. Please ensure the backend is running and accessible.');
        } else {
          throw new Error(`Network error: ${fetchErrorMsg}`);
        }
      }
      
      clearTimeout(timeoutId);
      
      let json: any;
      try {
        const text = await res.text();
        json = text ? JSON.parse(text) : {};
      } catch (parseErr) {
        console.error('[AuthContext.login] Response parse error:', parseErr);
        throw new Error('Server returned invalid response. Please try again.');
      }
      
      console.log('[AuthContext.login] Response:', { status: res.status, success: json.success });
      
      if (!res.ok) {
        const errorMsg = json.error?.message ?? json.error ?? `HTTP ${res.status}: Login failed`;
        console.warn('[AuthContext.login] Login rejected:', { status: res.status, error: errorMsg });
        setState(s => ({ ...s, isLoading: false, error: errorMsg }));
        throw new Error(errorMsg);
      }
      
      if (!json.success || !json.token) {
        const errorMsg = json.error?.message ?? json.error ?? json.message ?? 'No token received';
        console.warn('[AuthContext.login] Missing token in response');
        setState(s => ({ ...s, isLoading: false, error: errorMsg }));
        throw new Error(errorMsg);
      }
      
      // Set token on API client immediately so subsequent calls work
      apiClient.setToken(json.token);
      
      // Set Supabase session for persistence
      const { error: supabaseError } = await supabase.auth.setSession({
        access_token: json.token,
        refresh_token: json.token,
      });
      
      if (supabaseError) {
        console.warn('[AuthContext] setSession warning:', supabaseError);
        // Continue anyway - we have the token from backend
      }
      
      // Normalize user data from response
      const data = json.user;
      const firstName = data.first_name ?? data.profile?.first_name ?? '';
      const lastName = data.last_name ?? data.profile?.last_name ?? '';
      const avatarUrl = data.avatar_url ?? data.profile?.avatar_url ?? null;

      // Normalize role: handle both ResolvedUser (can_* flags) and shared AuthUser (permissions array) formats
      const isResolvedUser = 'can_manage_org' in (data.role || {});
      let normalizedRole: AuthUser['role'];
      if (isResolvedUser) {
        const r = data.role;
        const permissions: string[] = [
          ...(r.can_manage_org ? ['org:manage'] : []),
          ...(r.can_manage_employees ? ['employees:manage'] : []),
          ...(r.can_manage_payroll ? ['payroll:manage'] : []),
          ...(r.can_view_payroll ? ['payroll:view'] : []),
          ...(r.can_manage_leaves ? ['leaves:manage'] : []),
          ...(r.can_view_own_data ? ['view:own_data'] : []),
        ];
        normalizedRole = {
          id: r.id,
          name: r.name,
          display_name: r.display_name || (r.name ?? '').replace(/_/g, ' ').split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          permissions,
        };
      } else {
        normalizedRole = data.role && data.role.permissions ? data.role : {
          id: data.role?.id ?? '',
          name: data.role?.name ?? '',
          display_name: data.role?.display_name || (data.role?.name ?? '').replace(/_/g, ' ').split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          permissions: data.role?.permissions ?? [],
        };
      }

      const user: AuthUser = {
        id:              data.id,
        email:           data.email,
        first_name:      firstName,
        last_name:       lastName,
        avatar_url:      avatarUrl,
        organization_id: data.organization_id || data.organization?.id,
        employee_id:     data.employee_id || null,
        profile: { 
          first_name: firstName, 
          last_name: lastName, 
          avatar_url: avatarUrl, 
          employee_id: data.employee_id || null,
          role: normalizedRole 
        },
        role:         normalizedRole,
        organization: data.organization ?? {
          id: data.organization_id || '',
          name: '',
          slug: '',
          logo_url: null,
          base_currency: 'INR',
          primary_country: 'IN',
          subscription_tier: 'standard',
        },
      };
      
      console.log('[AuthContext.login] Login successful:', { userId: user.id });
      setState({ user, isLoading: false, isAuthenticated: true, error: null });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Login failed';
      console.error('[AuthContext.login] Error:', errorMsg);
      setState(s => ({ ...s, isLoading: false, error: errorMsg }));
      throw err;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    apiClient.setToken(null);
    await supabase.auth.signOut();
    if (channelRef.current) {
      await supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    setState({ user: null, isLoading: false, isAuthenticated: false, error: null });
  }, []);

  const refreshUser = useCallback(async (): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) await loadUser(session.access_token);
  }, [loadUser]);

  const can = useCallback((permission: string): boolean => {
    if (!state.user) return false;
    return buildCan(state.user.role)(permission);
  }, [state.user]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshUser, can }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hooks ─────────────────────────────────────────────────────────
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

export function usePermissions() {
  const { user, can } = useAuth();
  const r = user?.role;
  return {
    can,
    canManageOrg:       (r && can('org:manage'))            ?? false,
    canManageEmployees: (r && can('employees:manage'))      ?? false,
    canManagePayroll:   (r && can('payroll:manage'))        ?? false,
    canViewPayroll:     (r && can('payroll:view'))          ?? false,
    canManageLeaves:    (r && can('leaves:manage'))         ?? false,
    isSuperAdmin:       r?.name === 'super_admin',
    isHRManager:        r?.name === 'hr_manager',
    isHRAssistant:      r?.name === 'hr_assistant',
    isPayrollAdmin:     r?.name === 'payroll_admin',
    isEmployee:         r?.name === 'employee',
    isTeamLead:         r?.name === 'team_lead',
    isAuditor:          r?.name === 'auditor',
    isConsultant:       r?.name === 'consultant',
    /** True if the user is a platform-level admin (cross-org super-user) */
    isPlatformAdmin:    user?.is_platform_admin === true,
    roleName:           r?.name ?? '',
  };
}